import { DocumentArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';

interface ExportStockingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: { startDate: string | null; endDate: string | null };
}

type ExportFormat = 'pdf' | 'xlsx' | 'csv';
type ExportType = 'summary' | 'recommendations' | 'turnover' | 'optimization' | 'forecasts';

export const ExportStockingGuideModal: React.FC<ExportStockingGuideModalProps> = ({
  isOpen,
  onClose,
  dateRange,
}) => {
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [exportType, setExportType] = useState<ExportType>('summary');
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Build query parameters
      const params = new URLSearchParams({
        format,
        export_type: exportType,
      });

      // Add date filters if provided from global range
      if (dateRange.startDate) {
        params.append('date_from', new Date(dateRange.startDate).toISOString());
      }
      if (dateRange.endDate) {
        params.append('date_to', new Date(dateRange.endDate).toISOString());
      }

      // Make API request
      const apiBaseURL = apiService.getBaseUrl();
      const response = await fetch(
        `${apiBaseURL}/api/v1/stocking-guide/export?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `stocking-guide-${exportType}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Report exported successfully as ${format.toUpperCase()}`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-white rounded-t-xl md:rounded-lg w-full md:w-auto md:max-w-lg shadow-xl animate-slide-up md:animate-none">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DocumentArrowDownIcon className="w-6 h-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Export Stocking Guide</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isExporting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Export Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setFormat('pdf')}
                className={`px-4 py-3 border rounded-lg text-sm font-medium transition-all ${
                  format === 'pdf'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
                disabled={isExporting}
              >
                PDF
              </button>
              <button
                onClick={() => setFormat('xlsx')}
                className={`px-4 py-3 border rounded-lg text-sm font-medium transition-all ${
                  format === 'xlsx'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
                disabled={isExporting}
              >
                Excel
              </button>
              <button
                onClick={() => setFormat('csv')}
                className={`px-4 py-3 border rounded-lg text-sm font-medium transition-all ${
                  format === 'csv'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
                disabled={isExporting}
              >
                CSV
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {format === 'pdf' && 'Formatted report with tables and styling'}
              {format === 'xlsx' && 'Excel workbook with multiple sheets'}
              {format === 'csv' && 'Plain text comma-separated values (single table)'}
            </p>
          </div>

          {/* Export Type */}
          <div>
            <label htmlFor="export-type" className="block text-sm font-medium text-gray-700 mb-2">
              Data to Export
            </label>
            <select
              id="export-type"
              value={exportType}
              onChange={(e) => setExportType(e.target.value as ExportType)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isExporting}
            >
              <option value="summary">Summary (All Data)</option>
              <option value="recommendations">Stock Recommendations Only</option>
              <option value="turnover">Turnover Analysis Only</option>
              <option value="optimization">Profit Optimization Only</option>
              <option value="forecasts">Demand Forecasts Only</option>
            </select>
            {format === 'csv' && exportType === 'summary' && (
              <p className="text-xs text-yellow-600 mt-2 flex items-start">
                <span className="mr-1">⚠️</span>
                <span>
                  CSV format only exports one table. Select a specific data type for better results.
                </span>
              </p>
            )}
          </div>

          {/* Note about active filters */}
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-700">
            <p>Export will include data based on the currently selected date range.</p>
            {dateRange.startDate && (
              <p className="text-xs mt-1 font-medium">
                Range: {new Date(dateRange.startDate).toLocaleDateString()} -
                {dateRange.endDate ? new Date(dateRange.endDate).toLocaleDateString() : 'Present'}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportStockingGuideModal;
